/**
 * Helpers for composing WhatsApp template messages: parse an approved template's
 * structure, build the send `components` payload (header media + body params +
 * dynamic URL button param), and validate required inputs before sending.
 *
 * Meta requires an approved template for business-initiated messages; media headers
 * and dynamic URL buttons are runtime parameters supplied per send.
 */

import type { TemplateListItem } from '../WhatsApp/dialog360.types';

export type HeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | 'NONE';

export interface WaTemplateButton {
  type: string; // QUICK_REPLY | URL | PHONE_NUMBER
  text: string;
  url?: string | null;
  dynamic?: boolean; // URL contains {{n}}
}

export interface WaTemplateStructure {
  headerFormat: HeaderFormat;
  headerText: string;
  bodyText: string;
  bodyVarCount: number;
  footerText: string;
  buttons: WaTemplateButton[];
  /** Index (within buttons[]) of the dynamic URL button, or -1 if none. */
  dynamicUrlButtonIndex: number;
  requiresMedia: boolean;
}

const up = (s?: string | null) => String(s || '').toUpperCase();

export function maxPlaceholder(text: string): number {
  let max = 0;
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) max = Math.max(max, parseInt(m[1], 10));
  return max;
}

export function parseTemplate(item: TemplateListItem | null | undefined): WaTemplateStructure {
  const comps = item?.components ?? [];
  const header = comps.find((c) => up(c.type) === 'HEADER');
  const body = comps.find((c) => up(c.type) === 'BODY');
  const footer = comps.find((c) => up(c.type) === 'FOOTER');
  const buttonsComp = comps.find((c) => up(c.type) === 'BUTTONS');

  const headerFormat = (up(header?.format) || (header ? 'TEXT' : 'NONE')) as HeaderFormat;
  const bodyText = body?.text ?? '';
  const buttons: WaTemplateButton[] = (buttonsComp?.buttons ?? []).map((b) => ({
    type: up(b.type),
    text: b.text,
    url: b.url,
    dynamic: up(b.type) === 'URL' && maxPlaceholder(b.url ?? '') > 0,
  }));

  return {
    headerFormat,
    headerText: header?.text ?? '',
    bodyText,
    bodyVarCount: maxPlaceholder(bodyText),
    footerText: footer?.text ?? '',
    buttons,
    dynamicUrlButtonIndex: buttons.findIndex((b) => b.dynamic),
    requiresMedia: headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT',
  };
}

export interface ComposeValues {
  mediaUrl?: string;
  bodyValues?: string[];
  buttonUrlParam?: string;
}

/** Build the WhatsApp send `components` array (matches backend TemplateComponent). */
export function buildSendComponents(s: WaTemplateStructure, v: ComposeValues): any[] {
  const components: any[] = [];

  if (s.requiresMedia && v.mediaUrl) {
    const key = s.headerFormat.toLowerCase(); // image | video | document
    components.push({ type: 'header', parameters: [{ type: key, [key]: { link: v.mediaUrl } }] });
  }

  if (s.bodyVarCount > 0) {
    const vals = v.bodyValues ?? [];
    components.push({
      type: 'body',
      parameters: Array.from({ length: s.bodyVarCount }, (_, i) => ({ type: 'text', text: vals[i] ?? '' })),
    });
  }

  if (s.dynamicUrlButtonIndex >= 0 && v.buttonUrlParam) {
    components.push({
      type: 'button',
      subType: 'url',
      index: s.dynamicUrlButtonIndex,
      parameters: [{ type: 'text', text: v.buttonUrlParam }],
    });
  }

  return components;
}

/** Returns a list of human-readable validation errors (empty = valid). */
export function validateTemplateInputs(s: WaTemplateStructure, v: ComposeValues): string[] {
  const errs: string[] = [];
  if (s.requiresMedia && !v.mediaUrl) errs.push(`A ${s.headerFormat.toLowerCase()} header is required.`);
  for (let i = 0; i < s.bodyVarCount; i++) {
    if (!v.bodyValues?.[i]?.trim()) errs.push(`Body variable {{${i + 1}}} is required.`);
  }
  if (s.dynamicUrlButtonIndex >= 0 && !v.buttonUrlParam?.trim()) errs.push('The button URL value is required.');
  return errs;
}

export const mediaAccept = (format: HeaderFormat): string =>
  format === 'IMAGE' ? 'image/*' : format === 'VIDEO' ? 'video/*' : format === 'DOCUMENT' ? '.pdf,application/pdf' : '';
